import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsDown, ChevronsUp, Inbox, RefreshCw } from 'lucide-react';
import { Button, ErrorBoundary, Pagination, Skeleton } from '@shared/ui';
import { getOperationKey, type ServiceAccountingOperation } from '@entities/service-accounting';
import { OperationCard } from './operation-card';

interface OperationListPagination {
  page: number;
  limit: number;
  total: number;
  totalPages?: number;
}

interface OperationListProps {
  operations: ServiceAccountingOperation[];
  isLoading?: boolean;
  pagination?: OperationListPagination;
  onPageChange?: (page: number) => void;
  onRefresh?: () => void;
}

export function OperationList({
  operations,
  isLoading = false,
  pagination,
  onPageChange,
  onRefresh,
}: OperationListProps) {
  const { t } = useTranslation('cabinet');
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const operationKeys = useMemo(
    () => operations.map((operation, index) => getOperationKey(operation, index)),
    [operations],
  );

  useEffect(() => {
    setExpandedKeys(new Set());
  }, [operations]);

  const allExpanded = useMemo(
    () => operations.length > 0 && expandedKeys.size === operations.length,
    [operations.length, expandedKeys.size],
  );

  const toggleOperation = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    if (allExpanded) {
      setExpandedKeys(new Set());
      return;
    }

    setExpandedKeys(new Set(operationKeys));
  };

  const handleRelatedOperationClick = (operationId: string) => {
    const index = operations.findIndex((operation) => operation.id === operationId);
    if (index === -1) {
      return;
    }

    const key = getOperationKey(operations[index], index);
    setExpandedKeys((current) => new Set(current).add(key));
    requestAnimationFrame(() => {
      document.getElementById(`accounting-operation-${key}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  };

  const resultsCount = pagination?.total ?? operations.length;

  return (
    <div className="min-w-0 space-y-3 sm:space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-sans text-xs text-muted-foreground sm:text-sm">
          {t('serviceAccounting.resultsCount', { count: resultsCount })}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleToggleAll} disabled={!operations.length}>
            {allExpanded ? (
              <>
                <ChevronsUp className="mr-1.5 h-3.5 w-3.5" />
                {t('serviceAccounting.collapseAll')}
              </>
            ) : (
              <>
                <ChevronsDown className="mr-1.5 h-3.5 w-3.5" />
                {t('serviceAccounting.expandAll')}
              </>
            )}
          </Button>
          {onRefresh && (
            <Button
              variant="outline"
              size="icon"
              className="h-tap w-tap shrink-0 rounded-full sm:h-8 sm:w-8"
              onClick={onRefresh}
              loading={isLoading}
              leftIcon={<RefreshCw className="size-4" />}
              aria-label={t('common.retry', { ns: 'common' })}
            />
          )}
        </div>
      </div>

      {isLoading && !operations.length ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-14 w-full rounded-2xl" />
          ))}
        </div>
      ) : !operations.length ? (
        <div className="empty-state">
          <Inbox className="h-8 w-8 text-muted-foreground sm:h-10 sm:w-10" />
          <p className="section-description">{t('serviceAccounting.empty')}</p>
        </div>
      ) : (
        <div className="space-y-2 sm:space-y-3">
          {operations.map((operation, index) => {
            const operationKey = operationKeys[index];

            return (
              <div key={operationKey} id={`accounting-operation-${operationKey}`}>
                <ErrorBoundary
                  fallback={
                    <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                      {t('serviceAccounting.cardRenderError')}
                    </div>
                  }
                >
                  <OperationCard
                    operation={operation}
                    expanded={expandedKeys.has(operationKey)}
                    onToggle={() => toggleOperation(operationKey)}
                    onRelatedOperationClick={handleRelatedOperationClick}
                  />
                </ErrorBoundary>
              </div>
            );
          })}
        </div>
      )}

      {pagination && (
        <Pagination
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          onPageChange={(nextPage) => onPageChange?.(nextPage)}
        />
      )}
    </div>
  );
}
